import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const TYPE_CONFIG = {
  airtime: { icon: "ri-phone-line", color: "#059669", bg: "#ECFDF5", label: "Airtime Purchase" },
  data: { icon: "ri-wifi-line", color: "#0284C7", bg: "#EFF6FF", label: "Data Bundle" },
  electricity: { icon: "ri-flashlight-line", color: "#D97706", bg: "#FFFBEB", label: "Electricity Bill" },
  tv: { icon: "ri-tv-2-line", color: "#7C3AED", bg: "#F5F3FF", label: "TV Subscription" },
  internet: { icon: "ri-router-line", color: "#0F766E", bg: "#F0FDFA", label: "Internet Bill" },
  betting: { icon: "ri-gamepad-line", color: "#DC2626", bg: "#FEF2F2", label: "Betting Wallet" },
};

export default function BillConfirmPage() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [pin, setPin] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [pinError, setPinError] = useState("");

  if (!state) {
    navigate("/bills");
    return null;
  }

  const { type, title, detail, subDetail, amount, meta } = state;
  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.airtime;

  const handlePinInput = (value, index) => {
    if (!/^\d?$/.test(value)) return;
    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setPinError("");

    // Auto-focus next
    if (value && index < 3) {
      document.getElementById(`pin-${index + 1}`)?.focus();
    }
  };

  const handlePinKeyDown = (e, index) => {
    if (e.key === "Backspace" && !pin[index] && index > 0) {
      document.getElementById(`pin-${index - 1}`)?.focus();
    }
  };

  const pinComplete = pin.every(d => d !== "");

  const handleConfirm = async () => {
    if (!pinComplete) return;
    const enteredPin = pin.join("");

    // Simulate PIN validation (replace with real API)
    if (enteredPin !== "1234") {
      setPinError("Incorrect PIN. Please try again.");
      setPin(["", "", "", ""]);
      document.getElementById("pin-0")?.focus();
      return;
    }

    setLoading(true);
    // Simulate payment API call
    await new Promise(r => setTimeout(r, 2000));
    setLoading(false);

    navigate("/bills/success", {
      replace: true,
      state: { type, title, detail, amount, meta },
    });
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-10">
      {/* Header */}
      <div className="bg-white px-5 pt-12 pb-5 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center"
          >
            <i className="ri-arrow-left-line text-slate-600 text-lg" />
          </button>
          <div>
            <h1 className="text-slate-800 font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Confirm Payment
            </h1>
            <p className="text-slate-400 text-xs">Review before paying</p>
          </div>
        </div>
      </div>

      <div className="px-5 pt-6 space-y-5">
        {/* Bill Summary Card */}
        <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-sm">
          {/* Top accent */}
          <div className="h-1.5 w-full" style={{ background: cfg.color }} />

          <div className="p-5">
            {/* Icon + Title */}
            <div className="flex items-center gap-4 mb-5">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{ background: cfg.bg }}
              >
                <i className={`${cfg.icon} text-2xl`} style={{ color: cfg.color }} />
              </div>
              <div>
                <p className="text-xs text-slate-400 font-medium">{cfg.label}</p>
                <p className="text-slate-800 font-bold text-base" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {title}
                </p>
              </div>
            </div>

            {/* Details */}
            <div className="space-y-3 pb-5 border-b border-slate-100">
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">Recipient</span>
                <div className="text-right">
                  <p className="text-slate-800 font-semibold text-sm">{detail}</p>
                  {subDetail && <p className="text-slate-400 text-xs mt-0.5">{subDetail}</p>}
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">Service fee</span>
                <span className="text-slate-800 font-semibold text-sm">₦0.00</span>
              </div>
            </div>

            {/* Total */}
            <div className="flex justify-between items-center pt-4">
              <span className="text-slate-600 font-semibold text-sm">Total Amount</span>
              <span
                className="text-2xl font-bold"
                style={{ fontFamily: "'Space Grotesk', sans-serif", color: cfg.color }}
              >
                ₦{amount.toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        {/* PIN Entry */}
        <div className="bg-white rounded-3xl border border-slate-100 p-5 shadow-sm">
          <p className="text-slate-700 font-semibold text-sm mb-1 text-center">
            Enter your 4-digit PIN
          </p>
          <p className="text-slate-400 text-xs text-center mb-5">
            Confirm your identity to authorise this payment
          </p>

          <div className="flex justify-center gap-4 mb-4">
            {pin.map((digit, i) => (
              <input
                key={i}
                id={`pin-${i}`}
                type="password"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handlePinInput(e.target.value, i)}
                onKeyDown={(e) => handlePinKeyDown(e, i)}
                className={`w-14 h-14 text-center text-xl font-bold rounded-2xl border-2 outline-none transition-all ${
                  digit
                    ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                    : "border-slate-200 bg-slate-50 text-slate-800"
                } ${pinError ? "border-red-400 bg-red-50" : ""}`}
              />
            ))}
          </div>

          {pinError && (
            <p className="text-red-500 text-xs text-center mb-3">{pinError}</p>
          )}

          <p className="text-center text-xs text-slate-400">
            Forgot PIN?{" "}
            <button className="text-emerald-600 font-semibold underline-offset-2 underline">
              Reset
            </button>
          </p>
        </div>

        {/* Pay Button */}
        <button
          onClick={handleConfirm}
          disabled={!pinComplete || loading}
          className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 text-white py-4 rounded-2xl font-bold text-sm transition-all active:scale-95 flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              Processing payment…
            </>
          ) : (
            <>
              <i className="ri-lock-2-line" />
              Pay ₦{amount.toLocaleString()} Securely
            </>
          )}
        </button>

        <p className="text-center text-xs text-slate-400 pb-4">
          🔒 Secured by 256-bit SSL encryption
        </p>
      </div>
    </div>
  );
}
